import React, { useState, useEffect } from 'react';
import {
  Box, Container, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Card, CardContent, Grid, TextField,
  MenuItem, Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  Tabs, Tab, Select, FormControl, InputLabel, DateRangePicker, Stack, IconButton,
  Tooltip, Divider, TabContext, TabPanel
} from '@mui/material';
import {
  Info, Download, Search, Refresh, Edit, Delete, TrendingUp, TrendingDown,
  Error as ErrorIcon, CheckCircle, History, MoreVert
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { activityLogs } from '../services/api';

function ComprehensiveActivityPage() {
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('0'); // 0=All, 1=Errors, 2=Bot, 3=SL Updates, 4=Stats

  // Filters
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    symbol: '',
    mode: '',
    startDate: '',
    endDate: ''
  });

  const [selectedActivity, setSelectedActivity] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(50);

  useEffect(() => {
    fetchActivities();
    fetchStats();
  }, [filters, currentPage]);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const params = {
        limit: pageSize,
        offset: currentPage * pageSize,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      };

      const res = await activityLogs.list(params);
      setActivities(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load activities');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await activityLogs.stats();
      setStats(res.data.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setCurrentPage(0);
  };

  const handleExportCSV = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
      const res = await activityLogs.export(params);

      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity_logs_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success('✅ Activities exported to CSV');
    } catch (err) {
      toast.error('Failed to export');
    }
  };

  const openDetails = (activity) => {
    setSelectedActivity(activity);
    setDetailsOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'SUCCESS': return 'success';
      case 'FAILED': return 'error';
      case 'WARNING': return 'warning';
      case 'PARTIAL': return 'info';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'BOT_STATUS': return '🤖';
      case 'ORDER_EXECUTION': return '📝';
      case 'TRAILING_SL': return '📈';
      case 'AUTO_SELL': return '💰';
      case 'ERROR': return '❌';
      case 'POSITION_UPDATE': return '📊';
      default: return '📋';
    }
  };

  const StatCard = ({ title, value, icon: IconComponent }) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="caption">
              {title}
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {value || 0}
            </Typography>
          </Box>
          <IconComponent sx={{ fontSize: 32, color: 'primary.main', opacity: 0.5 }} />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>📊 Comprehensive Activity Log</Typography>
            <Typography variant="body2" color="textSecondary">
              Complete audit trail of all software operations, decisions, and failures
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleExportCSV}
              size="small"
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => { fetchActivities(); fetchStats(); }}
              size="small"
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Statistics Dashboard */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard title="Total Activities" value={stats.total_activities} icon={History} />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard title="Successful" value={stats.successful} icon={CheckCircle} />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard title="Failed" value={stats.failed} icon={ErrorIcon} />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard title="Orders" value={stats.orders} icon={TrendingUp} />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard title="Auto Sells" value={stats.auto_sells} icon={TrendingDown} />
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
          Filters & Search
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                label="Category"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="BOT_STATUS">Bot Status</MenuItem>
                <MenuItem value="ORDER_EXECUTION">Order Execution</MenuItem>
                <MenuItem value="TRAILING_SL">Trailing SL</MenuItem>
                <MenuItem value="AUTO_SELL">Auto Sell</MenuItem>
                <MenuItem value="TARGET_UPDATE">Target Update</MenuItem>
                <MenuItem value="ERROR">Errors</MenuItem>
                <MenuItem value="POSITION_UPDATE">Position Update</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="SUCCESS">Success</MenuItem>
                <MenuItem value="FAILED">Failed</MenuItem>
                <MenuItem value="WARNING">Warning</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Mode</InputLabel>
              <Select
                value={filters.mode}
                onChange={(e) => handleFilterChange('mode', e.target.value)}
                label="Mode"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PAPER">Paper</MenuItem>
                <MenuItem value="LIVE">Live</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Symbol"
              value={filters.symbol}
              onChange={(e) => handleFilterChange('symbol', e.target.value.toUpperCase())}
              placeholder="e.g., BANKNIFTY"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Start Date"
              type="datetime-local"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="End Date"
              type="datetime-local"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Activities Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : activities.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">No activities found</Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ overflowX: 'auto', mb: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell><strong>Time</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Action</strong></TableCell>
                  <TableCell><strong>Symbol</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Message</strong></TableCell>
                  <TableCell align="center"><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activities.map((activity) => (
                  <TableRow key={activity.id} hover sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(activity.timestamp).toLocaleString('en-IN')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{getCategoryIcon(activity.category)}</span>
                        <Chip
                          label={activity.category?.replace(/_/g, ' ')}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontWeight={600}>
                        {activity.action}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={activity.symbol || '-'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={activity.status}
                        size="small"
                        color={getStatusColor(activity.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={activity.message}
                      >
                        {activity.message}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<Info />}
                        onClick={() => openDetails(activity)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            <Button
              disabled={currentPage === 0}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </Button>
            <Typography sx={{ py: 1 }}>
              Page {currentPage + 1}
            </Typography>
            <Button
              disabled={activities.length < pageSize}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </Button>
          </Box>
        </>
      )}

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Activity Details</DialogTitle>
        <DialogContent>
          {selectedActivity && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                {/* Core Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600}>Core Information</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Timestamp</Typography>
                  <Typography variant="body2">
                    {new Date(selectedActivity.timestamp).toLocaleString('en-IN')}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Category</Typography>
                  <Chip label={selectedActivity.category} size="small" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Action</Typography>
                  <Chip label={selectedActivity.action} size="small" variant="outlined" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Status</Typography>
                  <Chip
                    label={selectedActivity.status}
                    size="small"
                    color={getStatusColor(selectedActivity.status)}
                  />
                </Grid>

                {/* Trade Details */}
                {selectedActivity.symbol && (
                  <>
                    <Grid item xs={12}>
                      <Divider />
                      <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>Trade Information</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="textSecondary">Symbol</Typography>
                      <Typography variant="body2">{selectedActivity.symbol}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="textSecondary">Type</Typography>
                      <Typography variant="body2">{selectedActivity.instrument_type || '-'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="textSecondary">Mode</Typography>
                      <Chip label={selectedActivity.mode} size="small" />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="textSecondary">Quantity</Typography>
                      <Typography variant="body2">{selectedActivity.quantity || '-'}</Typography>
                    </Grid>
                  </>
                )}

                {/* Price Information */}
                {(selectedActivity.entry_price || selectedActivity.sell_price) && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>Price Information</Typography>
                    </Grid>
                    {selectedActivity.entry_price && (
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">Entry Price</Typography>
                        <Typography variant="body2">₹{selectedActivity.entry_price}</Typography>
                      </Grid>
                    )}
                    {selectedActivity.sell_price && (
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">Sell Price</Typography>
                        <Typography variant="body2">₹{selectedActivity.sell_price}</Typography>
                      </Grid>
                    )}
                    {selectedActivity.initial_sl && (
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">Initial SL</Typography>
                        <Typography variant="body2">₹{selectedActivity.initial_sl}</Typography>
                      </Grid>
                    )}
                    {selectedActivity.initial_target && (
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">Initial Target</Typography>
                        <Typography variant="body2">₹{selectedActivity.initial_target}</Typography>
                      </Grid>
                    )}
                  </>
                )}

                {/* SL Update Details */}
                {selectedActivity.category === 'TRAILING_SL' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>Stop Loss Update</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="textSecondary">Previous SL</Typography>
                      <Typography variant="body2">₹{selectedActivity.previous_sl}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="textSecondary">New SL</Typography>
                      <Typography variant="body2">₹{selectedActivity.new_sl}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="textSecondary">Current Market Price</Typography>
                      <Typography variant="body2">₹{selectedActivity.current_market_price}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="textSecondary">Reason</Typography>
                      <Typography variant="body2">{selectedActivity.sl_reason}</Typography>
                    </Grid>
                  </>
                )}

                {/* P&L Information */}
                {selectedActivity.pnl !== null && selectedActivity.pnl !== undefined && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>Profit/Loss</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="textSecondary">P&L</Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: selectedActivity.pnl >= 0 ? '#4caf50' : '#f44336' }}
                      >
                        ₹{selectedActivity.pnl}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="textSecondary">P&L %</Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: selectedActivity.pnl_pct >= 0 ? '#4caf50' : '#f44336' }}
                      >
                        {selectedActivity.pnl_pct}%
                      </Typography>
                    </Grid>
                  </>
                )}

                {/* Error Details */}
                {selectedActivity.error_message && (
                  <>
                    <Grid item xs={12}>
                      <Divider />
                      <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>Error Details</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">Error Type</Typography>
                      <Chip label={selectedActivity.error_type} size="small" color="error" />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">Error Message</Typography>
                      <Alert severity="error" sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                          {selectedActivity.error_message}
                        </Typography>
                      </Alert>
                    </Grid>
                  </>
                )}

                {/* Full Message */}
                <Grid item xs={12}>
                  <Divider />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                    Message
                  </Typography>
                  <Paper sx={{ p: 1.5, bgcolor: '#f5f5f5', mt: 1 }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {selectedActivity.message}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ComprehensiveActivityPage;
